import { useCallback, useEffect, useRef, useState } from 'react';

import { createAdminProduct, getAdminProducts, type AdminProductRow, type PaginationMeta, type ProductFeatureDefinition, updateAdminProduct } from '../api/admin';

export function useAdminProducts(query?: string, page = 1, pageSize = 10) {
  const [products, setProducts] = useState<AdminProductRow[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>({ total: 0, page: 1, pageSize, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);

  const refreshingRef = useRef(false);

  const refresh = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setLoading(true);
    try {
      const response = await getAdminProducts({ q: query, page, pageSize });
      const payload = response.data.data as any;
      const items = Array.isArray(payload) ? payload : (payload?.items || []);
      const paginationMeta = payload?.pagination || {
        total: items.length,
        page,
        pageSize,
        totalPages: Math.max(1, Math.ceil(items.length / pageSize)),
      };
      setProducts(items);
      setPagination(paginationMeta);
      return items;
    } finally {
      setLoading(false);
      refreshingRef.current = false;
    }
  }, [page, pageSize, query]);

  useEffect(() => {
    refresh().catch(() => {
      setProducts([]);
      setPagination({ total: 0, page: 1, pageSize, totalPages: 1 });
    });
  }, [refresh]);

  const createProduct = useCallback(async (payload: {
    code: string;
    name: string;
    description?: string;
    status?: string;
    featureSchema?: ProductFeatureDefinition[];
  }) => {
    setCreating(true);
    try {
      await createAdminProduct(payload);
      refresh().catch(() => undefined);
    } finally {
      setCreating(false);
    }
  }, [refresh]);

  const saveProduct = useCallback(async (productId: number, payload: {
    name?: string;
    description?: string;
    status?: string;
    featureSchema?: ProductFeatureDefinition[];
  }) => {
    setUpdating(true);
    try {
      await updateAdminProduct(productId, payload);
      refresh().catch(() => undefined);
    } finally {
      setUpdating(false);
    }
  }, [refresh]);

  return { products, pagination, loading, creating, updating, refresh, createProduct, saveProduct };
}
